/**
 * BookingForm - Unified Service Request Form
 * 
 * Shared component for creating service requests across all apps.
 * Accepts dependencies as props to remain context-agnostic.
 * 
 * @param {Object} props
 * @param {Object} props.supabase - Supabase client instance (required)
 * @param {Object} props.customer - Customer data object
 * @param {string} props.mode - 'customer' or 'contractor'
 * @param {Function} props.onSubmit - Callback after successful submission
 * @param {Function} props.onClose - Callback to close the form
 * @param {Object} props.initialData - Initial form values
 * @param {string} props.theme - 'dark' or 'light' (default: 'dark')
 */
import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, MapPinIcon, ClockIcon, CurrencyDollarIcon, 
  PhotoIcon, TrashIcon, BoltIcon, ExclamationTriangleIcon, CheckCircleIcon 
} from '@heroicons/react/24/outline';
import StateSelect from './StateSelect';
import TagSelector from './TagSelector';
import EmergencyFeeDisclosure, { EmergencyFeeNotice } from './EmergencyFeeDisclosure';
import { compressImageForUpload } from '../utils/imageUpload';

const BookingForm = ({ 
  supabase,
  customer,
  bookingService, // Optional: createUnifiedBookingService(supabase) - if not provided, uses onSubmit
  mode = 'customer',
  onSubmit, 
  onClose,
  initialData = {},
  theme = 'dark'
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    selected_tags: [],
    pricing_preference: 'NEGOTIABLE',
    flat_rate: '',
    hourly_rate: '',
    request_type: 'STANDARD',
    service_mode: 'ONSITE',
    max_responses: 5,
    unlimited_responses: false,
    requires_inspection: false,
    preferred_time_option: 'specific',
    start_time: '',
    end_time: '',
    auto_booking: false,
    customer_name: mode === 'contractor' ? '' : (customer?.name || ''),
    customer_email: mode === 'contractor' ? '' : (customer?.email || ''),
    customer_phone: mode === 'contractor' ? '' : (customer?.phone || ''),
    location_address: customer?.street_address || '',
    location_city: customer?.city || '',
    location_state: customer?.state || '',
    location_postal_code: customer?.zip_code || '',
    ...initialData
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [emergencyFeeAcknowledged, setEmergencyFeeAcknowledged] = useState(false);
  const [showEmergencyDisclosure, setShowEmergencyDisclosure] = useState(false);
  const [emergencyContractorCount, setEmergencyContractorCount] = useState(0);

  // Theme-based styles - using outline only for selected cards (no background fill)
  const styles = theme === 'dark' ? {
    container: 'space-y-6',
    heading: 'text-white',
    subheading: 'text-lg font-medium text-white border-b border-gray-600 pb-2',
    label: 'text-sm font-medium text-gray-200',
    input: 'bg-slate-700/50 border-slate-600/50 placeholder-gray-500 text-white focus:ring-blue-500 focus:border-blue-500',
    cardSelected: 'border-blue-500 bg-slate-700/50',
    cardUnselected: 'border-gray-600 bg-slate-700/50 hover:border-gray-500',
    emergencyCardSelected: 'border-red-500 bg-slate-700/50',
    infoBox: 'bg-blue-500/10 border border-blue-500/30 rounded-lg p-3',
    infoText: 'text-sm text-blue-300',
    cancelBtn: 'border-gray-600 text-gray-300 bg-slate-700/50 hover:bg-slate-600/50',
    submitBtn: 'bg-blue-600 hover:bg-blue-700 text-white'
  } : {
    container: 'space-y-6',
    heading: 'text-gray-900',
    subheading: 'text-lg font-medium text-gray-900 border-b border-gray-200 pb-2',
    label: 'text-sm font-medium text-gray-700',
    input: 'bg-white border-gray-300 placeholder-gray-400 text-gray-900 focus:ring-blue-500 focus:border-blue-500',
    cardSelected: 'border-2 border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-md',
    cardUnselected: 'border-2 border-gray-300 bg-white hover:border-gray-400',
    emergencyCardSelected: 'border-2 border-red-500 bg-red-50 ring-2 ring-red-200 shadow-md',
    infoBox: 'bg-blue-50 border border-blue-200 rounded-lg p-3',
    infoText: 'text-sm text-blue-800',
    cancelBtn: 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
    submitBtn: 'bg-blue-600 hover:bg-blue-700 text-white'
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Recalculate emergency contractor count when selected tags change
  useEffect(() => {
    if (formData.request_type === 'EMERGENCY') {
      fetchEmergencyContractorCount();
    }
  }, [formData.selected_tags, formData.request_type]);

  // Fetch actual count of emergency-available contractors
  // NO TAGS = NO EXPANDED REACH FEE (count = 0)
  // WITH TAGS = Count contractors matching those tags who accept emergency
  const fetchEmergencyContractorCount = async () => {
    // ✅ No tags selected = no expanded reach fee
    if (!formData.selected_tags || formData.selected_tags.length === 0) {
      console.log('📊 No tags selected - no expanded reach fee');
      setEmergencyContractorCount(0);
      return;
    }

    try {
      // Get tag names to filter by
      const tagNames = formData.selected_tags.map(tag => tag.name || tag.label || tag).filter(Boolean);
      const tagsParam = tagNames.length > 0 ? `&tags=${encodeURIComponent(tagNames.join(','))}` : '';

      // Call the marketplace API with tag filter
      const apiBase = process.env.REACT_APP_MARKETPLACE_API_URL || '';
      const response = await fetch(`${apiBase}/api/companies?category=emergency_services&limit=100${tagsParam}`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const companyCount = data.companies?.length || data.length || 0;
      console.log('📊 Emergency contractor count (from API):', companyCount, 'for tags:', tagNames);
      setEmergencyContractorCount(companyCount);
    } catch (err) {
      console.error('Failed to fetch emergency contractor count:', err);
      // Fallback: try direct Supabase query (may be limited by RLS)
      try {
        const { count } = await supabase
          .from('companies')
          .select('id', { count: 'exact', head: true })
          .eq('accepts_emergency', true)
          .eq('is_active', true);
        console.log('📊 Emergency contractor count (fallback):', count || 0);
        setEmergencyContractorCount(count || 0);
      } catch (fallbackErr) {
        setEmergencyContractorCount(0);
      }
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum size is 10MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;
    setUploadingPhotos(true);

    try {
      // Ruling 4 (2026-11-02): photos are downscaled before upload — one at a time, so several
      // large photos are never decoded at once on a phone.
      const prepared = [];
      for (const original of validFiles) prepared.push(await compressImageForUpload(original));
      const uploadPromises = prepared.map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        
        const { data, error } = await supabase.storage
          .from('marketplace-photos')
          .upload(fileName, file);

        if (error) throw new Error(`Failed to upload ${file.name}`);

        const { data: { publicUrl } } = supabase.storage
          .from('marketplace-photos')
          .getPublicUrl(fileName);

        return {
          name: file.name,
          url: publicUrl,
          preview: URL.createObjectURL(file),
          storageKey: fileName
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setUploadedPhotos(prev => [...prev, ...uploadedFiles]);
      e.target.value = '';
    } catch (error) {
      console.error('Photo upload failed:', error);
      alert(error.message || 'Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const removePhoto = async (index) => {
    const photo = uploadedPhotos[index];
    try {
      if (photo.storageKey) {
        await supabase.storage.from('marketplace-photos').remove([photo.storageKey]);
      }
      if (photo.preview) URL.revokeObjectURL(photo.preview);
      setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error removing photo:', error);
      setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.title.trim()) return alert('Please enter a title');
    if (!formData.description.trim()) return alert('Please enter a description');
    if (formData.selected_tags.length === 0) return alert('Please select at least one service tag');

    if (formData.pricing_preference === 'FLAT' && (!formData.flat_rate || parseFloat(formData.flat_rate) <= 0)) {
      return alert('Please enter a valid flat rate amount');
    }
    if (formData.pricing_preference === 'HOURLY' && (!formData.hourly_rate || parseFloat(formData.hourly_rate) <= 0)) {
      return alert('Please enter a valid hourly rate');
    }
    if (formData.preferred_time_option === 'specific' && !formData.start_time) {
      return alert('Please select a preferred start time when choosing "Pick Dates"');
    }
    if (mode === 'customer') {
      if (!formData.customer_name.trim()) return alert('Please enter your name');
      if (!formData.customer_email.trim()) return alert('Please enter your email');
    }

    setSubmitting(true);

    try {
      const requestData = {
        customer_id: customer?.customer_id || customer?.id || null,
        auth_user_id: customer?.auth_user_id || null,
        title: formData.title.trim(),
        description: formData.description.trim(),
        request_type: (formData.request_type || 'STANDARD').toUpperCase(),
        service_mode: formData.service_mode || 'ONSITE',
        pricing_preference: (formData.pricing_preference || 'NEGOTIABLE').toUpperCase(),
        flat_rate: formData.pricing_preference === 'FLAT' ? parseFloat(formData.flat_rate) : null,
        hourly_rate: formData.pricing_preference === 'HOURLY' ? parseFloat(formData.hourly_rate) : null,
        location_address: formData.location_address,
        location_city: formData.location_city,
        location_state: formData.location_state,
        location_postal_code: formData.location_postal_code,
        start_time: formData.preferred_time_option === 'specific' ? formData.start_time : null,
        end_time: formData.preferred_time_option === 'specific' ? formData.end_time : null,
        preferred_time_option: formData.preferred_time_option || 'flexible',
        max_responses: formData.unlimited_responses ? null : formData.max_responses,
        unlimited_responses: formData.unlimited_responses,
        requires_inspection: formData.requires_inspection || false,
        photo_urls: uploadedPhotos.map(photo => photo.url)
      };

      // Only the customer flow needs a customer_id. In contractor mode the requester is the
      // contractor's company — identity comes from the authenticated session (requesterId =
      // auth.uid()) inside UnifiedBookingService, so a customer_id is neither present nor needed.
      if (mode === 'customer' && !requestData.customer_id) {
        alert('Unable to identify customer account. Please refresh and try again.');
        return;
      }

      const tagNames = formData.selected_tags.map(tag => tag.name);

      if (bookingService) {
        const newRequest = await bookingService.createRequest(requestData, tagNames);
        if (onSubmit) await onSubmit(newRequest);
      } else if (onSubmit) {
        await onSubmit({ ...requestData, tagNames });
      }

      if (onClose) onClose();
    } catch (error) {
      console.error('Booking submission error:', error);
      alert(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Safety check for supabase client — AFTER every hook and every handler definition.
  // rules-of-hooks: hooks must run in the same order on every render, so this early return
  // cannot sit above the useEffect. It also cannot sit between the useEffect and
  // fetchEmergencyContractorCount, because that effect closes over the const below it.
  // Same fix, same reason, as customer-portal RequestService.js:61.
  if (!supabase) {
    return (
      <div className="p-6 text-center rounded-2xl border border-gray-200">
        <h3 className="text-lg font-medium mb-2">Service Unavailable</h3>
        <p className="text-gray-600 mb-4">
          Unable to connect to the service. Please refresh the page or try again later.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Refresh Page
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className={`text-xl font-semibold ${styles.heading}`}>
            {mode === 'contractor' ? 'Request Service from Another Provider' : 'Request Service'}
          </h2>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close" title="Close" className="text-gray-400 hover:text-gray-300">
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className={styles.subheading}>Service Details</h3>

          <div>
            <label htmlFor="title" className={`block ${styles.label}`}>Service Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`}
              placeholder="Brief description of what you need"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className={`block ${styles.label}`}>Detailed Description *</label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`}
              placeholder="Provide detailed information about the service you need..."
              required
            />
          </div>

          {/* Service Tags */}
          <div>
            <label htmlFor="booking-service-categories" className={`block ${styles.label} mb-2`}>Service Categories *</label>
            <TagSelector
              inputId="booking-service-categories"
              required
              supabase={supabase}
              selectedTags={formData.selected_tags}
              onTagsChange={(tags) => setFormData(prev => ({ ...prev, selected_tags: tags }))}
              placeholder="Select service categories..."
              theme={theme}
            />
          </div>
        </div>

        {/* Customer Information - Only for customer mode */}
        {mode === 'customer' && (
          <div className="space-y-4">
            <h3 className={styles.subheading}>Contact Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="customer_name" className={`block ${styles.label}`}>Full Name *</label>
                <input type="text" id="customer_name" name="customer_name" value={formData.customer_name} onChange={handleChange}
                  className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} required />
              </div>
              <div>
                <label htmlFor="customer_phone" className={`block ${styles.label}`}>Phone Number</label>
                <input type="tel" id="customer_phone" name="customer_phone" value={formData.customer_phone} onChange={handleChange}
                  className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} />
              </div>
            </div>
            <div>
              <label htmlFor="customer_email" className={`block ${styles.label}`}>Email Address *</label>
              <input type="email" id="customer_email" name="customer_email" value={formData.customer_email} onChange={handleChange}
                className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} required />
            </div>
          </div>
        )}

        {/* Timing & Workflow */}
        <div className="space-y-6">
          <h4 className={styles.subheading}>
            <ClockIcon className="h-5 w-5 inline mr-2" />
            Timing & Workflow
          </h4>
          <div>
            <label className={`block ${styles.label} mb-3`}>When would you like this service?</label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 mb-4">
              {[
                { value: 'anytime', label: 'Anytime', icon: '📅' },
                { value: 'soonest', label: 'Soonest Available', icon: '⚡' },
                { value: 'this_week', label: 'This Week', icon: '📆' },
                { value: 'weekend_only', label: 'Weekend Only', icon: '🏖️' },
                { value: 'specific', label: 'Pick Dates', icon: '🎯' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, preferred_time_option: option.value }))}
                  className={`p-3 text-center border rounded-lg transition-all ${
                    formData.preferred_time_option === option.value
                      ? styles.cardSelected
                      : styles.cardUnselected
                  }`}
                >
                  <div className="text-lg mb-1">{option.icon}</div>
                  <div className="text-xs font-medium">{option.label}</div>
                </button>
              ))}
            </div>
          </div>

          {formData.preferred_time_option === 'specific' && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="start_time" className={`block ${styles.label}`}>Preferred Start *</label>
                <input type="datetime-local" id="start_time" name="start_time" value={formData.start_time} onChange={handleChange}
                  required={formData.preferred_time_option === 'specific'}
                  className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} />
              </div>
              <div>
                <label htmlFor="end_time" className={`block ${styles.label}`}>Preferred End (Optional)</label>
                <input type="datetime-local" id="end_time" name="end_time" value={formData.end_time} onChange={handleChange}
                  className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} />
              </div>
            </div>
          )}

          {formData.preferred_time_option && formData.preferred_time_option !== 'specific' && (
            <div className={styles.infoBox}>
              <div className={styles.infoText}>
                {formData.preferred_time_option === 'anytime' && <p>📅 <strong>Anytime:</strong> Contractors can schedule during their normal business hours.</p>}
                {formData.preferred_time_option === 'soonest' && <p>⚡ <strong>Soonest Available:</strong> Contractors will offer their earliest available time slot.</p>}
                {formData.preferred_time_option === 'this_week' && <p>📆 <strong>This Week:</strong> Service will be scheduled within the next 7 days.</p>}
                {formData.preferred_time_option === 'weekend_only' && <p>🏖️ <strong>Weekend Only:</strong> Service will be scheduled on Saturday or Sunday only.</p>}
              </div>
            </div>
          )}
        </div>

        {/* Pricing Preferences */}
        <div className="space-y-4">
          <h4 className={styles.subheading}>
            <CurrencyDollarIcon className="h-5 w-5 inline mr-2" />
            Pricing Preferences
          </h4>
          <div>
            <label className={`block ${styles.label} mb-3`}>How would you like to handle pricing?</label>
            <div className="space-y-3">
              {[
                { value: 'NEGOTIABLE', label: 'Negotiable', description: 'Open to contractor proposals' },
                { value: 'FLAT', label: 'Flat Rate', description: 'Fixed price for the entire job' },
                { value: 'HOURLY', label: 'Hourly Rate', description: 'Pay by the hour worked' }
              ].map((option) => (
                <label key={option.value} className="flex items-start">
                  <input type="radio" name="pricing_preference" value={option.value}
                    checked={formData.pricing_preference === option.value} onChange={handleChange}
                    className="mt-1 focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-500 bg-slate-700" />
                  <div className="ml-3">
                    <div className={`text-sm font-medium ${styles.heading}`}>{option.label}</div>
                    <div className="text-sm text-gray-400">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {formData.pricing_preference === 'FLAT' && (
            <div>
              <label htmlFor="flat_rate" className={`block ${styles.label}`}>Maximum Budget *</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 sm:text-sm">$</span>
                </div>
                <input type="number" id="flat_rate" name="flat_rate" value={formData.flat_rate} onChange={handleChange}
                  className={`appearance-none block w-full pl-8 pr-12 py-2 border rounded-md sm:text-sm ${styles.input}`}
                  placeholder="0.00" min="0" step="0.01" />
              </div>
            </div>
          )}

          {formData.pricing_preference === 'HOURLY' && (
            <div>
              <label htmlFor="hourly_rate" className={`block ${styles.label}`}>Maximum Hourly Rate *</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 sm:text-sm">$</span>
                </div>
                <input type="number" id="hourly_rate" name="hourly_rate" value={formData.hourly_rate} onChange={handleChange}
                  className={`appearance-none block w-full pl-8 pr-12 py-2 border rounded-md sm:text-sm ${styles.input}`}
                  placeholder="0.00" min="0" step="0.01" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 sm:text-sm">/hr</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Service Location */}
        <div className="space-y-4">
          <h4 className={styles.subheading}>
            <MapPinIcon className="h-5 w-5 inline mr-2" />
            Service Location
          </h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="location_address" className={`block ${styles.label}`}>Street Address</label>
              <input type="text" id="location_address" name="location_address" value={formData.location_address} onChange={handleChange}
                className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} placeholder="123 Main Street" />
            </div>
            <div>
              <label htmlFor="location_city" className={`block ${styles.label}`}>City</label>
              <input type="text" id="location_city" name="location_city" value={formData.location_city} onChange={handleChange}
                className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} />
            </div>
            <div>
              <label htmlFor="location_state" className={`block ${styles.label}`}>State</label>
              <StateSelect id="location_state" name="location_state" value={formData.location_state} onChange={handleChange}
                className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} placeholder="Select state" />
            </div>
            <div>
              <label htmlFor="location_postal_code" className={`block ${styles.label}`}>Zip Code *</label>
              <input type="text" id="location_postal_code" name="location_postal_code" value={formData.location_postal_code} onChange={handleChange}
                required className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md sm:text-sm ${styles.input}`} placeholder="12345" />
            </div>
          </div>
        </div>

        {/* Request Type Cards — after location/tags so contractor count is accurate */}
          <div>
            <label className={`block ${styles.label} mb-3`}>Request Type *</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => { setFormData(prev => ({ ...prev, request_type: 'STANDARD' })); setEmergencyFeeAcknowledged(false); }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  formData.request_type === 'STANDARD' ? styles.cardSelected : styles.cardUnselected
                }`}
              >
                <ClockIcon className="h-8 w-8 text-blue-400 mb-2" />
                <div className={`text-sm font-medium ${styles.heading}`}>Standard Request</div>
                <div className="text-xs text-gray-400 mt-1">Normal priority service request</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({ ...prev, request_type: 'EMERGENCY' }));
                  if (!emergencyFeeAcknowledged) {
                    fetchEmergencyContractorCount();
                    setShowEmergencyDisclosure(true);
                  }
                }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  formData.request_type === 'EMERGENCY' ? styles.emergencyCardSelected : styles.cardUnselected
                }`}
              >
                <BoltIcon className="h-8 w-8 text-red-400 mb-2" />
                <div className={`text-sm font-medium ${styles.heading} flex items-center gap-2`}>
                  Emergency Request
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-400" />
                </div>
                <div className="text-xs text-gray-400 mt-1">Urgent service needed immediately</div>
                <div className="text-xs text-amber-400 mt-1">Emergency fee applies</div>
              </button>
            </div>

            {formData.request_type === 'EMERGENCY' && (
              <div className="mt-4">
                <EmergencyFeeNotice
                  onLearnMore={() => setShowEmergencyDisclosure(true)}
                  estimatedContractors={emergencyContractorCount}
                  theme={theme}
                />
                {emergencyFeeAcknowledged && (
                  <div className="mt-2 flex items-center text-green-400 text-sm">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Fee acknowledged
                  </div>
                )}
              </div>
            )}
          </div>

          <EmergencyFeeDisclosure
            isOpen={showEmergencyDisclosure}
            onClose={() => setShowEmergencyDisclosure(false)}
            onConfirm={() => { setEmergencyFeeAcknowledged(true); setShowEmergencyDisclosure(false); }}
            onChooseStandard={() => {
              setFormData(prev => ({ ...prev, request_type: 'STANDARD' }));
              setEmergencyFeeAcknowledged(false);
              setShowEmergencyDisclosure(false);
            }}
            estimatedContractors={emergencyContractorCount}
            theme={theme}
          />

        {/* Response Limit */}
        <div className="space-y-3">
          <h4 className={styles.subheading}>
            How many contractors can respond?
          </h4>
          <p className="text-sm text-gray-300">Control how many bids you receive. More responses = more options, fewer = less noise.</p>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 3, label: '3', desc: 'Focused' },
              { value: 5, label: '5', desc: 'Recommended' },
              { value: 10, label: '10', desc: 'Wide net' },
              { value: 0, label: 'Unlimited', desc: 'All available' },
            ].map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  max_responses: option.value || 999,
                  unlimited_responses: option.value === 0
                }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                  (option.value === 0 && formData.unlimited_responses) ||
                  (option.value > 0 && !formData.unlimited_responses && formData.max_responses === option.value)
                    ? 'border-blue-500 bg-blue-600 text-white shadow-md ring-2 ring-blue-300'
                    : theme === 'dark'
                      ? 'border-gray-600 bg-slate-700/50 text-gray-300 hover:border-gray-500'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <span className="font-medium">{option.label}</span>
                <span className="ml-1 text-xs opacity-70">({option.desc})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Photo Upload */}
        <div className="space-y-4">
          <h4 className={styles.subheading}>
            <PhotoIcon className="h-5 w-5 inline mr-2" />
            Photos (Optional)
          </h4>
          <p className="text-sm text-gray-300">Upload photos to help contractors better understand your project needs.</p>

          <div className="flex items-center justify-center w-full">
            <label htmlFor="photo-upload" className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${styles.cardUnselected}`}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <PhotoIcon className="w-8 h-8 mb-2 text-gray-400" />
                <p className="mb-2 text-sm text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
              </div>
              <input id="photo-upload" type="file" multiple accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhotos} />
            </label>
          </div>

          {uploadingPhotos && (
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                <span className="text-sm text-blue-300">Uploading photos...</span>
              </div>
            </div>
          )}

          {uploadedPhotos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {uploadedPhotos.map((photo, index) => (
                <div key={index} className="relative group">
                  <img src={photo.preview} alt={`Upload ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-gray-600" />
                  <button type="button" onClick={() => removePhoto(index)}
                    className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <TrashIcon className="h-3 w-3" />
                  </button>
                  <div className="absolute bottom-1 left-1 right-1">
                    <div className="text-xs text-white bg-black/50 rounded px-1 py-0.5 truncate">{photo.name}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          {onClose && (
            <button type="button" onClick={onClose}
              className={`px-4 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors ${styles.cancelBtn}`}>
              Cancel
            </button>
          )}
          <button type="submit" disabled={submitting}
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium disabled:opacity-50 transition-colors ${styles.submitBtn}`}>
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;
